import { z } from 'zod';
import { auditSchema, idSchema } from '../../common.js';
/**
 * Apps tab (WS-16) — NotePad + SketchPad, built on the WS-15 sharing foundation. The
 * registry/audience shape is copied deliberately from `lists` (docs/DECISIONS.md
 * 2026-08-01 "WS-15 sharing circles"): a family registry doc holds every family-visible
 * tool's metadata, a per-user registry (`apptools:user:<id>`, enforced by *name* —
 * `@awwyeah/shared/sharing`) holds private/member-shared ones, and each tool's own content
 * doc mirrors its meta so a member it's shared *with* — who cannot open somebody else's
 * personal registry — can still resolve its title. See `apps/web/src/modules/apps/`.
 */
/** Yjs doc holding `Y.Map<toolId, AppToolMeta>` — the family-visible registry. */
export const APPTOOLS_DOC = 'apptools';
/** One Yjs doc per NotePad. */
export function noteDocName(id) {
    return `note:${id}`;
}
/** One Yjs doc per SketchPad. */
export function sketchDocName(id) {
    return `sketch:${id}`;
}
export const APP_KINDS = ['note', 'sketch'];
export const appKindSchema = z.enum(APP_KINDS);
export const appToolMetaSchema = auditSchema.extend({
    id: idSchema,
    kind: appKindSchema,
    title: z.string().min(1).max(200),
    emoji: z.string().max(8).optional(),
});
/* -------------------------------------------------------------------------- */
/* SketchPad stroke model                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Width presets, as a fraction of the canvas's shorter side. Both `width` and every point
 * in `points` are normalized (0..1) against the canvas's *current* size at draw time, so a
 * stroke drawn on a phone renders at the same relative weight and position on a desktop —
 * "all screen sizes render identically" (WS-16 brief).
 */
export const STROKE_WIDTHS = [0.006, 0.014, 0.026];
/**
 * Eight theme-independent swatches. Explicit hex, not design tokens: a stroke drawn today
 * must look the same after the household switches themes tomorrow, which is also why
 * "eraser" is a delete-stroke tool rather than a paint-with-background-color one (a white
 * eraser would punch a hole in a dark theme).
 */
export const SKETCH_COLOR_SWATCHES = [
    '#1f2937',
    '#ef4444',
    '#f97316',
    '#eab308',
    '#22c55e',
    '#0ea5e9',
    '#6366f1',
    '#ec4899',
];
export const strokeSchema = z.object({
    id: idSchema,
    authorId: idSchema,
    color: z.string().min(1).max(32),
    width: z.number().positive(),
    /** Flat `x0,y0,x1,y1,…`, normalized 0..1 against the canvas size at draw time. */
    points: z.array(z.number()).min(2),
    createdAt: z.string(),
});
/** Pixel points (flat x,y pairs) → normalized 0..1 against the given canvas size. */
export function normalizePoints(points, width, height) {
    const out = [];
    for (let i = 0; i + 1 < points.length; i += 2) {
        out.push((points[i] ?? 0) / width, (points[i + 1] ?? 0) / height);
    }
    return out;
}
/** Normalized 0..1 points → pixel points against the given canvas size. */
export function denormalizePoints(points, width, height) {
    const out = [];
    for (let i = 0; i + 1 < points.length; i += 2) {
        out.push((points[i] ?? 0) * width, (points[i + 1] ?? 0) * height);
    }
    return out;
}
export function strokeBoundingBox(points) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i + 1 < points.length; i += 2) {
        const x = points[i] ?? 0;
        const y = points[i + 1] ?? 0;
        if (x < minX)
            minX = x;
        if (x > maxX)
            maxX = x;
        if (y < minY)
            minY = y;
        if (y > maxY)
            maxY = y;
    }
    return { minX, minY, maxX, maxY };
}
function distanceToSegment(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq === 0)
        return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
/**
 * Hit-tests a tap/drag point (normalized 0..1, same space as `stroke.points`) against a
 * stroke: bounding-box reject first (cheap, covers the common "nowhere near it" case),
 * then point-to-segment distance against every segment, widened by half the stroke's own
 * width plus `padding` (a little forgiveness for a fingertip vs. a thin line).
 */
export function hitTestStroke(stroke, x, y, padding = 0.015) {
    const reach = stroke.width / 2 + padding;
    const box = strokeBoundingBox(stroke.points);
    if (x < box.minX - reach ||
        x > box.maxX + reach ||
        y < box.minY - reach ||
        y > box.maxY + reach) {
        return false;
    }
    const pts = stroke.points;
    if (pts.length < 4) {
        return Math.hypot(x - (pts[0] ?? 0), y - (pts[1] ?? 0)) <= reach;
    }
    for (let i = 0; i + 3 < pts.length; i += 2) {
        const d = distanceToSegment(x, y, pts[i] ?? 0, pts[i + 1] ?? 0, pts[i + 2] ?? 0, pts[i + 3] ?? 0);
        if (d <= reach)
            return true;
    }
    return false;
}
//# sourceMappingURL=index.js.map